import React from 'react';
import styled from '@emotion/styled';
import { Button, ButtonColor, ButtonSize } from '@components/material-ui';
import { ThemeProps } from '@theme/base';
import { mq } from '@styles/breakpoints';
import { mixins } from '@styles/mixins';
import { Icons } from '@model/common/icons';
import { TestId } from '@components/test-ids';
import { PrimarySpinner } from '@components/common/spinner';
import { Icon } from '@components/common/generic-icon';
import { useI18NextContext } from '@components/hooks';

export interface LoadMoreProps {
  isLoadingMore: boolean;
  onLoadMore: () => void;
  isMoreAvailable: boolean;
}

const LoadMoreContainer = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: `0 0 ${theme.custom.spacing.medium}px ${theme.custom.spacing.xLarge}px`,
  paddingTop: 0,
  marginTop: theme.custom.spacing.xLarge,
  flexDirection: 'column',
  width: '100%',
  position: 'relative',
  [mq.medium]: {
    flexDirection: 'row',
    marginTop: theme.custom.spacing.xxLarge
  }
}));

const LoadMoreButton = styled(Button)({
  width: '100%',
  [mq.medium]: {
    width: 350
  }
});

const SpinnerContainer = styled.div({
  width: 350,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const UpArrow = styled(Icon)({
  fontSize: '1.2rem'
});

const BackToTop = styled.div(({ theme }: ThemeProps) => ({
  marginTop: theme.custom.spacing.xLarge,
  ...(theme.custom.typography.paragraph as any),
  ...mixins(theme).link,
  i: {
    ...mixins(theme).link,
    marginLeft: theme.custom.spacing.xSmall
  },
  ':hover': {
    i: {
      color: theme.custom.states.link.hover
    }
  },
  [mq.medium]: {
    position: 'absolute',
    right: 0,
    marginTop: 0,
    paddingRight: theme.custom.spacing.medium
  }
}));

export const LoadMore = ({ isLoadingMore, onLoadMore, isMoreAvailable }: LoadMoreProps) => {
  const t: any = useI18NextContext();
  const handleOnScrollUp = () => {
    if (top) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const label: string = isMoreAvailable ? 'deal-finder__results--view-more' : 'deal-finder__results--end-of-results';
  return (
    <LoadMoreContainer>
      {!isLoadingMore && (
        <LoadMoreButton
          testId={TestId.dealFinder.results.loadMore}
          label={label}
          onClick={onLoadMore}
          color={ButtonColor.SECONDARY}
          size={ButtonSize.SMALL}
          disabled={!isMoreAvailable}
        />
      )}
      {isLoadingMore && (
        <SpinnerContainer>
          <PrimarySpinner size={40} />
        </SpinnerContainer>
      )}
      <BackToTop onClick={handleOnScrollUp}>
        {t('deal-finder__results--to-top')}
        <UpArrow name={Icons.ARROW_UP} />
      </BackToTop>
    </LoadMoreContainer>
  );
};
